import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';
import { removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#clearHeaderDrawerSwitching);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#clearHeaderDrawerSwitching);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    this.#closeHeaderDrawer();

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    if (this.refs.dialog?.open) {
      this.closeDialog();
      return;
    }

    this.#closeHeaderDrawer();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  showDialog() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    this.#syncHeaderSheetPosition();
    this.classList.remove('cart-drawer--closing');
    document.body.classList.remove('cart-drawer-closing');

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.show();
      this.#setTriggerOpenState(true);
      dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.classList.add('cart-drawer--closing');
    document.body.classList.add('cart-drawer-closing');

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await Promise.race([onAnimationEnd(dialog, undefined, { subtree: false }), new Promise((resolve) => setTimeout(resolve, 420))]);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.classList.remove('cart-drawer--closing');
    document.body.classList.remove('cart-drawer-closing');
    this.#setTriggerOpenState(false);
    this.#clearHeaderSheetPosition();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * @param {boolean} isOpen
   */
  #setTriggerOpenState(isOpen) {
    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (!(trigger instanceof HTMLElement)) return;

    trigger.setAttribute('aria-expanded', String(isOpen));

    const label = trigger.getAttribute(isOpen ? 'data-close-label' : 'data-cart-label');
    if (label) {
      trigger.setAttribute('aria-label', label);
    }
  }

  #handleClick = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (event.target instanceof Node && dialog.contains(event.target)) return;

    this.closeDialog();
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  #syncHeaderSheetPosition() {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const headerRect = header.getBoundingClientRect();
    const headerHeight = Math.max(0, Math.round(headerRect.height));
    const headerTop = Math.max(0, Math.round(headerRect.top));

    document.body.style.setProperty('--header-sheet-top', `${headerHeight}px`);
    document.body.style.setProperty('--header-sheet-surface-top', `${headerTop}px`);
  }

  #clearHeaderSheetPosition() {
    document.body.style.removeProperty('--header-sheet-top');
    document.body.style.removeProperty('--header-sheet-surface-top');
  }

  #closeHeaderDrawer() {
    const details = document.querySelector('#Details-menu-drawer-container');
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    details.classList.add('menu-switching');
    details.classList.remove('menu-open');
    details.removeAttribute('open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus();
  }

  #clearHeaderDrawerSwitching = () => {
    document.querySelector('#Details-menu-drawer-container')?.classList.remove('menu-switching');
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
